import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, {
  DocumentSignSigningSessionResponse,
  DocumentSignSigningFieldSubmission,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Bootstraps the signing session — single fetch that powers the entire
 * SigningPage. Token comes straight from the URL; we never persist it.
 */
export function useSigningSession(token: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.documentSign.signingSession(token ?? '__none__'),
    queryFn: () => api.getDocumentSignSigningSession(token as string),
    enabled: !!token,
    // Recipient view should never refetch silently — every refetch costs the
    // recipient a `RecipientViewed` event with their IP/UA. Fresh-once.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function useAcceptConsent(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.acceptDocumentSignConsent(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.signingSession(token) });
    },
  });
}

export function useSubmitFields(token: string) {
  return useMutation({
    mutationFn: (fields: DocumentSignSigningFieldSubmission[]) =>
      api.submitDocumentSignFields(token, { fields }),
  });
}

export function useSubmitSignature(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.submitDocumentSignSubmit(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.signingSession(token) });
    },
  });
}

export function useDeclineSignature(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.declineDocumentSignSignature(token, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.signingSession(token) });
    },
  });
}

export type SigningSessionData = DocumentSignSigningSessionResponse;
